// src/pages/admin/AdminCoaches.tsx
import React, { useState, useEffect, useMemo } from "react";

import {
  doc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { sendPasswordResetEmail } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, auth } from "../../firebase/config";
import { getAllUsers, invalidateUsersCache } from "../../firebase/users";
import Navbar from "../../components/Navbar";
import { useTranslation } from "../../hooks/useTranslation";
import { C, FONT } from "../../theme";
import { formatDate as formatDateLocalized } from "../../utils/dateHelpers";

// ── Responsive hook ───────────────────────────────────────────────────────────
function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

type UserRole = "coach" | "admin" | "ranking_admin";
type RoleFilter = "all" | UserRole;

const ROLE_OPTIONS: { value: UserRole; label: string }[] = [
  { value: "coach",         label: "Coach" },
  { value: "admin",         label: "Admin" },
  { value: "ranking_admin", label: "Ranking Admin" },
];

const ROLE_BADGE: Record<UserRole, { bg: string; color: string; border: string }> = {
  coach:         { bg: C.blueDim,  color: C.blue,    border: C.blue    },
  admin:         { bg: C.redDim,   color: C.red,     border: C.red     },
  ranking_admin: { bg: "#ede9fe",  color: "#7c3aed", border: "#7c3aed" },
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UserAccount {
  uid: string;
  displayName: string;
  email: string;
  role: UserRole;
  club?: string;
  phone?: string;
  createdAt?: any;
}

interface FormState {
  displayName: string;
  email: string;
  password: string;
  role: UserRole;
  club: string;
  phone: string;
}

const EMPTY_FORM: FormState = {
  displayName: "",
  email: "",
  password: "",
  role: "coach",
  club: "",
  phone: "",
};

// ---------------------------------------------------------------------------
// Role badge component
// ---------------------------------------------------------------------------

function RoleBadge({ role }: { role: UserRole }) {
  const style = ROLE_BADGE[role] ?? ROLE_BADGE.coach;
  const label =
    role === "ranking_admin" ? "Ranking Admin" : role === "admin" ? "Admin" : "Coach";
  return (
    <span
      style={{
        display: "inline-block",
        backgroundColor: style.bg,
        color: style.color,
        border: `1px solid ${style.border}`,
        borderRadius: "20px",
        padding: "2px 10px",
        fontSize: "11px",
        fontWeight: 700,
        letterSpacing: "0.3px",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AdminCoaches() {
  const { t } = useTranslation();
  const w = useWindowWidth();
  const isMobile = w < 768;

  const [users, setUsers] = useState<UserAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");

  // Modal state
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState<UserAccount | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  // Delete confirm
  const [confirmDelete, setConfirmDelete] = useState<UserAccount | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Toast
  const [toast, setToast] = useState("");
  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3500);
  };

  // ── Fetch ALL users — cached via firebase/users.ts (no orderBy so docs
  //    without displayName are still included; we sort client-side).
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const all = (await getAllUsers()).map((u) => u as unknown as UserAccount);
      // Clone before sort — withCache returns a shared reference; sorting in
      // place would mutate the cache for other consumers.
      const sorted = [...all].sort(
        (a, b) => (a.displayName ?? "").localeCompare(b.displayName ?? "")
      );
      setUsers(sorted);
    } catch (err) {
      console.error("Failed to load users:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // ── Derived counts ────────────────────────────────────────────────────────
  const coachCount        = users.filter((u) => u.role === "coach").length;
  const adminCount        = users.filter((u) => u.role === "admin").length;
  const rankingAdminCount = users.filter((u) => u.role === "ranking_admin").length;

  // ── Filtered + searched list ──────────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = users;

    if (roleFilter !== "all") {
      list = list.filter((u) => u.role === roleFilter);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (u) =>
          u.displayName?.toLowerCase().includes(q) ||
          u.email?.toLowerCase().includes(q) ||
          u.club?.toLowerCase().includes(q) ||
          u.role?.toLowerCase().includes(q)
      );
    }

    return list;
  }, [users, roleFilter, search]);

  // ── Form helpers ──────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditUser(null);
    setForm(EMPTY_FORM);
    setFormError("");
    setShowForm(true);
  };

  const openEdit = (user: UserAccount) => {
    setEditUser(user);
    setForm({
      displayName: user.displayName ?? "",
      email: user.email ?? "",
      password: "",
      role: user.role ?? "coach",
      club: user.club ?? "",
      phone: user.phone ?? "",
    });
    setFormError("");
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditUser(null);
    setFormError("");
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setFormError("");
    if (!form.displayName.trim()) { setFormError("Display name is required."); return; }
    if (!form.email.trim())       { setFormError("Email is required."); return; }
    if (!editUser && !form.password.trim()) {
      setFormError("Password is required for a new account."); return;
    }
    if (!editUser && form.password.length < 6) {
      setFormError("Password must be at least 6 characters."); return;
    }

    setSubmitting(true);
    try {
      if (editUser) {
        await updateDoc(doc(db, "users", editUser.uid), {
          displayName: form.displayName.trim(),
          role: form.role,
          club: form.club.trim() || null,
          phone: form.phone.trim() || null,
        });
        invalidateUsersCache();
        setUsers((prev) =>
          prev
            .map((u) =>
              u.uid === editUser.uid
                ? {
                    ...u,
                    displayName: form.displayName.trim(),
                    role: form.role,
                    club: form.club.trim() || undefined,
                    phone: form.phone.trim() || undefined,
                  }
                : u
            )
            .sort((a, b) => (a.displayName ?? "").localeCompare(b.displayName ?? ""))
        );
        showToast("Account updated successfully.");
      } else {
        // ── Create user via Cloud Function (atomic Auth + Firestore) ─────
        let uid: string;
        try {
          const functions = getFunctions(undefined, "europe-central2");
          const createAppUserFn = httpsCallable(functions, "createAppUser");
          const result = await createAppUserFn({
            email: form.email.trim(),
            password: form.password,
            displayName: form.displayName.trim(),
            role: form.role,
            club: form.club.trim(),
            phone: form.phone.trim(),
          });
          uid = (result.data as { uid: string }).uid;
        } catch (err: any) {
          // httpsCallable surfaces HttpsError as { code, message }
          // where code looks like "functions/already-exists".
          const code: string = err?.code ?? "";
          const message: string = err?.message ?? "";
          if (code === "functions/already-exists" || message === "EMAIL_EXISTS") {
            setFormError(t("admin.coaches.error.emailExists"));
          } else {
            setFormError(`Auth error: ${message || code || "unknown"}`);
          }
          return;
        }

        invalidateUsersCache();

        // Password reset email is fire-and-forget; failure is non-fatal.
        try {
          await sendPasswordResetEmail(auth, form.email.trim());
        } catch {
          // Non-fatal
        }

        // Mirror the just-created doc into local state so the table updates
        // without a full reload. Timestamp.now() is a close-enough stand-in
        // for the serverTimestamp the Cloud Function wrote — used only for
        // the createdAt column display until the next refetch.
        const userData = {
          displayName: form.displayName.trim(),
          email: form.email.trim().toLowerCase(),
          role: form.role,
          club: form.club.trim() || undefined,
          phone: form.phone.trim() || undefined,
          createdAt: Timestamp.now(),
        };
        setUsers((prev) =>
          [...prev, { uid, ...userData }].sort((a, b) =>
            (a.displayName ?? "").localeCompare(b.displayName ?? "")
          )
        );
        showToast(
          `Account created (${form.role}). Password reset email sent to ${form.email.trim()}.`
        );
      }
      closeForm();
    } catch (err) {
      console.error("Save failed:", err);
      setFormError(t("admin.coaches.error.save"));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Delete (Auth + Firestore via Cloud Function) ─────────────────────────
  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      const functions = getFunctions(undefined, "europe-central2");
      const deleteAppUserFn = httpsCallable(functions, "deleteAppUser");
      await deleteAppUserFn({ uid: confirmDelete.uid });
      invalidateUsersCache();
      setUsers((prev) => prev.filter((u) => u.uid !== confirmDelete.uid));
      showToast("Account fully removed (Auth + Firestore).");
      setConfirmDelete(null);
    } catch (err: any) {
      console.error("Delete failed:", err);
      const msg = err?.message ?? err?.code ?? "";
      alert(msg ? `${t("admin.coaches.error.delete")}\n${msg}` : t("admin.coaches.error.delete"));
    } finally {
      setDeleting(false);
    }
  };

  // ── Password reset ────────────────────────────────────────────────────────
  const handleResetPassword = async (user: UserAccount) => {
    try {
      await sendPasswordResetEmail(auth, user.email);
      showToast(`Password reset email sent to ${user.email}`);
    } catch {
      alert("Failed to send password reset email.");
    }
  };

  // ── Format date ───────────────────────────────────────────────────────────
  const formatDate = (ts: any): string => formatDateLocalized(ts);

  // ── Tab config ────────────────────────────────────────────────────────────
  const tabs: { key: RoleFilter; label: string; count: number; color?: string }[] = [
    { key: "all",           label: t("admin.users.tab.all"),           count: users.length        },
    { key: "coach",         label: t("admin.users.tab.coaches"),       count: coachCount,         color: C.blue    },
    { key: "ranking_admin", label: t("admin.users.tab.rankingAdmins"), count: rankingAdminCount,  color: "#7c3aed" },
    { key: "admin",         label: t("admin.users.tab.admins"),        count: adminCount,         color: C.red     },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={s.page} className="page-content">
      <Navbar />
      <div style={{ ...s.container, padding: isMobile ? "20px 16px" : "40px 24px" }}>

        {/* ── Header ── */}
        <div
          style={{
            ...s.headerRow,
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? "12px" : undefined,
            alignItems: isMobile ? "flex-start" : "center",
          }}
        >
          <div>
            <div style={s.pageLabel}>{t("admin.users.pageLabel")}</div>
            <div style={{ ...s.heading, fontSize: isMobile ? "26px" : undefined }}>
              {t("admin.users.heading")}
            </div>
            <div style={s.subheading}>{t("admin.users.subheading")}</div>
          </div>
          <button style={s.addBtn} onClick={openAdd}>
            {t("admin.users.addBtn")}
          </button>
        </div>

        {/* ── Role filter tabs ── */}
        <div style={{ ...s.tabRow, flexWrap: "wrap" as const }}>
          {tabs.map((tab) => {
            const active = roleFilter === tab.key;
            const activeColor = tab.color ?? C.red;
            return (
              <button
                key={tab.key}
                style={{
                  ...s.tab,
                  ...(active
                    ? {
                        borderColor: activeColor,
                        color: activeColor,
                        backgroundColor: activeColor + "18",
                      }
                    : {}),
                }}
                onClick={() => setRoleFilter(tab.key)}
              >
                {tab.label}
                <span
                  style={{
                    ...s.tabBadge,
                    backgroundColor: active ? activeColor + "22" : C.surface2,
                    color: active ? activeColor : C.text3,
                  }}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* ── Search bar ── */}
        <div style={{ marginBottom: "20px" }}>
          <input
            style={{ ...s.searchInput, width: "100%", boxSizing: "border-box" }}
            placeholder={t("admin.coaches.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* ── Loading ── */}
        {loading && <div style={s.stateBox}>{t("admin.coaches.loading")}</div>}

        {/* ── Empty collection ── */}
        {!loading && users.length === 0 && (
          <div style={s.emptyBox}>{t("admin.coaches.empty")}</div>
        )}

        {/* ── No match ── */}
        {!loading && users.length > 0 && filtered.length === 0 && (
          <div style={s.stateBox}>{t("admin.coaches.noMatch")}</div>
        )}

        {/* ── Table ── */}
        {!loading && filtered.length > 0 && (
          <div style={{ ...s.tableWrapper, WebkitOverflowScrolling: "touch" as any }}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>{t("admin.coaches.table.name")}</th>
                  <th style={s.th}>{t("admin.coaches.table.email")}</th>
                  <th style={s.th}>Role</th>
                  <th style={s.th}>{t("admin.coaches.table.club")}</th>
                  <th style={s.th}>{t("admin.coaches.table.created")}</th>
                  <th style={{ ...s.th, textAlign: "right" }}>
                    {t("admin.coaches.table.actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((user) => (
                  <tr key={user.uid} style={s.row}>
                    <td style={{ ...s.td, fontWeight: 600, color: C.text }}>
                      {user.displayName || "—"}
                    </td>
                    <td style={s.td}>{user.email}</td>
                    <td style={s.td}>
                      <RoleBadge role={user.role ?? "coach"} />
                    </td>
                    <td style={s.td}>{user.club || "—"}</td>
                    <td style={s.td}>{formatDate(user.createdAt)}</td>
                    <td style={{ ...s.td, textAlign: "right" }}>
                      <div style={s.actionCell}>
                        <button
                          style={s.iconBtn}
                          title={t("admin.coaches.resetPassword")}
                          onClick={() => handleResetPassword(user)}
                        >
                          🔑
                        </button>
                        <button style={s.editBtn} onClick={() => openEdit(user)}>
                          {t("admin.coaches.edit")}
                        </button>
                        <button style={s.deleteBtn} onClick={() => setConfirmDelete(user)}>
                          {t("admin.coaches.delete")}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Add / Edit Modal ── */}
      {showForm && (
        <div style={s.overlay} onClick={closeForm}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <div style={s.modalTitle}>
                {editUser
                  ? t("admin.coaches.form.editTitle")
                  : t("admin.coaches.form.addTitle")}
              </div>
              <button style={s.closeBtn} onClick={closeForm}>✕</button>
            </div>
            <div style={s.modalBody}>

              {/* Display Name */}
              <div style={s.field}>
                <label style={s.label}>{t("admin.coaches.form.displayName")}</label>
                <input
                  style={s.input}
                  value={form.displayName}
                  onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
                  placeholder="Giorgi Beridze"
                  autoFocus
                />
              </div>

              {/* Email */}
              <div style={s.field}>
                <label style={s.label}>{t("admin.coaches.form.email")}</label>
                <input
                  style={{
                    ...s.input,
                    ...(editUser ? { opacity: 0.5, cursor: "not-allowed" } : {}),
                  }}
                  value={form.email}
                  onChange={(e) =>
                    !editUser && setForm((f) => ({ ...f, email: e.target.value }))
                  }
                  readOnly={!!editUser}
                  placeholder="user@example.com"
                  type="email"
                />
                {editUser && (
                  <div style={s.fieldNote}>
                    Email cannot be changed here. Use Firebase Console.
                  </div>
                )}
              </div>

              {/* Password — new accounts only */}
              {!editUser && (
                <div style={s.field}>
                  <label style={s.label}>{t("admin.coaches.form.password")}</label>
                  <input
                    style={s.input}
                    value={form.password}
                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                    type="password"
                    placeholder="Min. 6 characters"
                  />
                  <div style={s.fieldNote}>{t("admin.coaches.passwordNote")}</div>
                </div>
              )}

              {/* Role */}
              <div style={s.field}>
                <label style={s.label}>Role</label>
                <select
                  style={s.input}
                  value={form.role}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, role: e.target.value as UserRole }))
                  }
                >
                  {ROLE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <div style={s.fieldNote}>
                  {form.role === "coach" &&
                    "Can register athletes and pay via BOG or prepaid credits."}
                  {form.role === "admin" &&
                    "Full access — athletes, clubs, competitions, registrations, rankings."}
                  {form.role === "ranking_admin" &&
                    "Limited access — athletes, clubs, competitions, rankings only. No registrations or credits."}
                </div>
              </div>

              <div style={s.fieldGrid}>
                {/* Club */}
                <div style={s.field}>
                  <label style={s.label}>{t("admin.coaches.form.club")}</label>
                  <input
                    style={s.input}
                    value={form.club}
                    onChange={(e) => setForm((f) => ({ ...f, club: e.target.value }))}
                    placeholder="Rustavi TKD"
                  />
                </div>
                {/* Phone */}
                <div style={s.field}>
                  <label style={s.label}>{t("admin.coaches.form.phone")}</label>
                  <input
                    style={s.input}
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="+995 5xx xxx xxx"
                  />
                </div>
              </div>

              {formError && <div style={s.error}>{formError}</div>}

              <div style={s.modalActions}>
                <button style={s.cancelBtn} onClick={closeForm} disabled={submitting}>
                  {t("admin.coaches.cancel")}
                </button>
                <button
                  style={submitting ? s.submitBtnDisabled : s.submitBtn}
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting
                    ? editUser ? t("admin.coaches.saving") : t("admin.coaches.creating")
                    : editUser ? t("admin.coaches.save") : t("admin.coaches.create")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ── */}
      {confirmDelete && (
        <div style={s.overlay} onClick={() => setConfirmDelete(null)}>
          <div style={{ ...s.modal, maxWidth: "420px" }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <div style={s.modalTitle}>{t("admin.coaches.deleteConfirm")}</div>
              <button style={s.closeBtn} onClick={() => setConfirmDelete(null)}>✕</button>
            </div>
            <div style={s.modalBody}>
              <div style={{ color: C.text, marginBottom: "4px", fontWeight: 600 }}>
                {confirmDelete.displayName}
              </div>
              <div style={{ marginBottom: "8px" }}>
                <RoleBadge role={confirmDelete.role ?? "coach"} />
              </div>
              <div style={{ color: C.text2, fontSize: "13px", marginBottom: "16px" }}>
                {confirmDelete.email}
              </div>
              <div style={s.deleteWarning}>{t("admin.coaches.deleteConfirmMsg")}</div>
              <div style={s.modalActions}>
                <button
                  style={s.cancelBtn}
                  onClick={() => setConfirmDelete(null)}
                  disabled={deleting}
                >
                  {t("admin.coaches.cancel")}
                </button>
                <button
                  style={deleting ? s.submitBtnDisabled : s.dangerBtn}
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? "Deleting…" : t("admin.coaches.delete")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast ── */}
      {toast && <div style={s.toast}>{toast}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const s: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", backgroundColor: "transparent" },
  container: { maxWidth: "1100px", margin: "0 auto", padding: "40px 24px" },

  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "24px",
    flexWrap: "wrap",
    gap: "16px",
  },
  pageLabel: {
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "2px",
    color: C.red,
    marginBottom: "6px",
    textTransform: "uppercase",
  },
  heading: {
    fontFamily: FONT.display,
    fontSize: "42px",
    letterSpacing: "2px",
    color: C.text,
    lineHeight: 1,
    marginBottom: "8px",
  },
  subheading: { fontSize: "14px", color: C.text2 },

  addBtn: {
    backgroundColor: C.red,
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
    alignSelf: "center",
    whiteSpace: "nowrap",
  },

  // ── Tabs ──
  tabRow: {
    display: "flex",
    gap: "8px",
    marginBottom: "16px",
  },
  tab: {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    padding: "8px 16px",
    borderRadius: "8px",
    border: `1px solid ${C.border}`,
    backgroundColor: C.surface,
    color: C.text2,
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.15s",
    whiteSpace: "nowrap",
  },
  tabBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "20px",
    height: "20px",
    borderRadius: "10px",
    padding: "0 6px",
    fontSize: "11px",
    fontWeight: 700,
  },

  searchInput: {
    padding: "9px 13px",
    border: `1px solid ${C.border2}`,
    borderRadius: "8px",
    fontSize: "14px",
    color: C.text,
    backgroundColor: C.surface2,
    outline: "none",
  },

  stateBox: {
    textAlign: "center",
    padding: "60px 20px",
    color: C.text2,
    fontSize: "14px",
  },
  emptyBox: {
    textAlign: "center",
    padding: "60px 20px",
    backgroundColor: C.surface,
    border: `1px dashed ${C.border2}`,
    borderRadius: "12px",
    color: C.text2,
    fontSize: "14px",
  },

  tableWrapper: {
    overflowX: "auto",
    borderRadius: "12px",
    border: `1px solid ${C.border}`,
    backgroundColor: C.surface,
  },
  table: { width: "100%", borderCollapse: "collapse", minWidth: "700px" },
  th: {
    textAlign: "left",
    padding: "12px 16px",
    backgroundColor: C.surface2,
    color: C.text2,
    fontWeight: 700,
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: `1px solid ${C.border}`,
  },
  row: { borderBottom: `1px solid ${C.border}` },
  td: {
    padding: "12px 16px",
    color: C.text2,
    fontSize: "13px",
    verticalAlign: "middle",
  },
  actionCell: {
    display: "flex",
    gap: "8px",
    justifyContent: "flex-end",
    flexWrap: "wrap",
  },
  iconBtn: {
    background: "none",
    border: `1px solid ${C.border2}`,
    borderRadius: "6px",
    padding: "5px 8px",
    fontSize: "14px",
    cursor: "pointer",
  },
  editBtn: {
    backgroundColor: C.surface2,
    color: C.text2,
    border: `1px solid ${C.border2}`,
    borderRadius: "6px",
    padding: "5px 12px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  deleteBtn: {
    backgroundColor: C.redDim,
    color: C.red,
    border: `1px solid ${C.red}`,
    borderRadius: "6px",
    padding: "5px 12px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  // Modal
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "20px",
  },
  modal: {
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: "14px",
    width: "100%",
    maxWidth: "520px",
    boxShadow: "0 28px 80px rgba(0,0,0,0.35)",
    maxHeight: "90vh",
    overflowY: "auto",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 24px 16px",
    borderBottom: `1px solid ${C.border}`,
  },
  modalTitle: {
    fontFamily: FONT.display,
    fontSize: "20px",
    color: C.text,
    letterSpacing: "0.5px",
  },
  closeBtn: {
    background: "none",
    border: `1px solid ${C.border2}`,
    borderRadius: "6px",
    color: C.text3,
    fontSize: "16px",
    cursor: "pointer",
    padding: "4px 8px",
    lineHeight: 1,
  },
  modalBody: {
    padding: "20px 24px 24px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  field: { display: "flex", flexDirection: "column", gap: "6px" },
  fieldGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" },
  label: {
    fontSize: "10px",
    fontWeight: 700,
    color: C.text2,
    textTransform: "uppercase",
    letterSpacing: "0.8px",
  },
  fieldNote: { fontSize: "11px", color: C.text3, marginTop: "2px" },
  input: {
    padding: "9px 12px",
    border: `1px solid ${C.border2}`,
    borderRadius: "7px",
    fontSize: "14px",
    color: C.text,
    backgroundColor: C.surface2,
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  error: {
    backgroundColor: C.redDim,
    border: `1px solid rgba(218,41,28,0.3)`,
    borderRadius: "7px",
    padding: "10px 14px",
    fontSize: "13px",
    color: C.red,
  },
  deleteWarning: {
    backgroundColor: C.redDim,
    border: `1px solid ${C.red}`,
    borderRadius: "8px",
    padding: "12px 14px",
    fontSize: "13px",
    color: C.red,
    marginBottom: "4px",
    lineHeight: 1.5,
  },
  modalActions: {
    display: "flex",
    gap: "10px",
    justifyContent: "flex-end",
    marginTop: "4px",
  },
  cancelBtn: {
    backgroundColor: C.surface2,
    color: C.text2,
    border: `1px solid ${C.border2}`,
    borderRadius: "7px",
    padding: "10px 20px",
    fontSize: "14px",
    cursor: "pointer",
    fontWeight: 500,
  },
  submitBtn: {
    backgroundColor: C.red,
    color: "#fff",
    border: "none",
    borderRadius: "7px",
    padding: "10px 24px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
  },
  submitBtnDisabled: {
    backgroundColor: C.border,
    color: C.text3,
    border: "none",
    borderRadius: "7px",
    padding: "10px 24px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "not-allowed",
  },
  dangerBtn: {
    backgroundColor: C.red,
    color: "#fff",
    border: "none",
    borderRadius: "7px",
    padding: "10px 24px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
  },

  // Toast
  toast: {
    position: "fixed",
    bottom: "28px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: C.surface3,
    color: C.text,
    padding: "12px 24px",
    borderRadius: "10px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
    zIndex: 2000,
    whiteSpace: "nowrap",
  },
};
